/**
 * P-04/P-05 套餐与权益编辑（docs/03-UIUX.md §5）。
 *
 * 选择产品后查看套餐列表，点击套餐进入编辑面板。
 * 权益编辑支持添加/删除/修改 key-value。
 */
import { useCallback, useState } from 'react';
import { Icon } from '../icons/Icon';
import { EmptyFirstRun, ErrorBlock } from '../components/DataState';
import { describeError } from '../api/client';
import { createPlan, listLicenses, listProducts, listPlans, getPlan, updatePlan } from '../api/endpoints';
import type { Plan } from '../api/types';
import { useAsync } from '../lib/useAsync';
import { ICON_SIZE } from '../tokens';
import type { Product } from '../api/types';

/** 将离线宽限秒数格式化为「X 天 Y 小时」，用于表单辅助提示。 */
function formatGraceHint(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0 && hours > 0) return `${days} 天 ${hours} 小时`;
  if (days > 0) return `${days} 天`;
  return `${hours} 小时`;
}

export function PlansPage() {
  const products = useAsync(useCallback(() => listProducts(), []), []);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const plans = useAsync(
    useCallback(() => listPlans(selectedProductId || undefined), [selectedProductId]),
    [selectedProductId],
  );

  const productList: readonly Product[] = products.state.kind === 'ready' ? products.state.data.items : [];
  const planList: readonly Plan[] = plans.state.kind === 'ready' ? plans.state.data.items : [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">套餐与权益</h1>
          <p className="page-head__sub">
            编辑套餐参数与权益配置。修改后已存在的授权不受影响（使用套餐快照）。
          </p>
        </div>
        <div className="page-head__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setShowCreate(true)}
          >
            <Icon name="plus" size={ICON_SIZE.inline} />
            新建套餐
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <div className="filter-bar__group">
          <label className="filter-bar__label">产品</label>
          <select
            className="filter-bar__select"
            value={selectedProductId}
            onChange={(e) => { setSelectedProductId(e.target.value); setEditingPlanId(null); }}
          >
            <option value="">全部产品</option>
            {productList.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {plans.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(plans.state.error, '加载套餐')} onRetry={plans.reload} />
        </div>
      ) : plans.state.kind === 'loading' ? (
        <div className="card-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <div className="skeleton-bar" style={{ height: 120 }} />
          <div className="skeleton-bar" style={{ height: 120 }} />
        </div>
      ) : planList.length === 0 ? (
        <div className="table-frame">
          <EmptyFirstRun
            icon="layers"
            description={selectedProductId ? '该产品暂无套餐。创建第一个套餐来定义授权参数。' : '暂无套餐。创建第一个套餐来定义授权参数。'}
            action={
              <button type="button" className="btn btn--primary" onClick={() => setShowCreate(true)}>
                <Icon name="plus" size={ICON_SIZE.inline} />
                新建套餐
              </button>
            }
          />
        </div>
      ) : (
        <div className="plan-cards">
          {planList.map((plan) => (
            <div key={plan.id} className="plan-card" style={{ cursor: 'pointer' }} onClick={() => setEditingPlanId(plan.id)}>
              <div className="plan-card__head">
                <span className="plan-card__name">{plan.name}</span>
                <span className="plan-card__code"><code>{plan.code}</code></span>
                <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
                  {productList.find((p) => p.id === plan.product_id)?.name ?? plan.product_id.slice(0, 8)}
                </span>
                <span className={`status-dot status-dot--${plan.is_active ? 'active' : 'archived'}`} />
                <button type="button" className="btn btn--quiet" style={{ marginLeft: 'auto' }} onClick={(e) => { e.stopPropagation(); setEditingPlanId(plan.id); }}>
                  <Icon name="settings-2" size={ICON_SIZE.inline} />
                  编辑
                </button>
              </div>
              <div className="plan-card__body">
                <div className="plan-card__stat">
                  <span className="plan-card__stat-value">{typeof plan.max_devices === 'number' ? plan.max_devices : '—'}</span>
                  <span className="plan-card__stat-label">设备上限</span>
                </div>
                <div className="plan-card__stat">
                  <span className="plan-card__stat-value">
                    {plan.duration_days === null ? '永久' : typeof plan.duration_days === 'number' ? `${plan.duration_days} 天` : '—'}
                  </span>
                  <span className="plan-card__stat-label">有效期</span>
                </div>
                <div className="plan-card__stat">
                  <span className="plan-card__stat-value">
                    {typeof plan.offline_grace_seconds === 'number' && Number.isFinite(plan.offline_grace_seconds)
                      ? (plan.offline_grace_seconds >= 86400
                          ? `${Math.floor(plan.offline_grace_seconds / 86400)} 天`
                          : `${Math.floor(plan.offline_grace_seconds / 3600)} 小时`)
                      : '—'}
                  </span>
                  <span className="plan-card__stat-label">离线宽限</span>
                </div>
              </div>
              {plan.entitlements && Object.keys(plan.entitlements).length > 0 && (
                <div className="plan-card__entitlements">
                  {Object.entries(plan.entitlements).map(([key, value]) => (
                    <span key={key} className="tag">{key}: {String(value)}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editingPlanId && (
        <PlanEditPanel planId={editingPlanId} onClose={() => setEditingPlanId(null)} onSaved={() => { setEditingPlanId(null); plans.reload(); }} />
      )}

      {showCreate && (
        <CreatePlanModal
          initialProductId={selectedProductId || undefined}
          products={productList}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); plans.reload(); }}
        />
      )}
    </>
  );
}

interface CreatePlanModalProps {
  readonly initialProductId?: string;
  readonly products: readonly Product[];
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function CreatePlanModal({ initialProductId, products, onClose, onCreated }: CreatePlanModalProps) {
  const [selectedProductId, setSelectedProductId] = useState(initialProductId ?? '');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [maxDevices, setMaxDevices] = useState(1);
  const [durationDays, setDurationDays] = useState<number | null>(365);
  const [offlineGrace, setOfflineGrace] = useState(604800);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!selectedProductId || !code.trim() || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await createPlan(selectedProductId, {
        code: code.trim(),
        name: name.trim(),
        max_devices: maxDevices,
        duration_days: durationDays,
        offline_grace_seconds: offlineGrace,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel__head">
          <h2 className="panel__title">新建套餐</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <div className="field">
            <label>产品</label>
            <select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)}>
              <option value="">选择产品…</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>套餐代码 (code)</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="pro"
              maxLength={63}
            />
            <p className="text-muted" style={{ fontSize: 12 }}>
              小写字母/数字/下划线，用于授权码前缀，如 &quot;pro&quot;
            </p>
          </div>

          <div className="field">
            <label>套餐名称</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Pro Plan" maxLength={120} />
          </div>

          <div className="field">
            <label>设备上限</label>
            <input type="number" min={1} max={1000} value={maxDevices} onChange={(e) => setMaxDevices(Math.min(1000, Math.max(1, Number(e.target.value) || 1)))} />
          </div>

          <div className="field">
            <label>有效期（天，留空=永久）</label>
            <input type="number" min={0} value={durationDays ?? ''} onChange={(e) => setDurationDays(e.target.value ? Number(e.target.value) : null)} placeholder="留空=永久" />
          </div>

          <div className="field">
            <label>离线宽限（秒）</label>
            <input type="number" min={21600} value={offlineGrace} onChange={(e) => setOfflineGrace(Math.max(21600, Number(e.target.value) || 21600))} />
            <p className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
              ≈ {formatGraceHint(offlineGrace)}（断网容错窗口，每次在线校验成功自动续期）
            </p>
          </div>

          {error && (
            <p className="login-error">
              <Icon name="octagon-x" size={ICON_SIZE.inline} />
              {error}
            </p>
          )}

          <button
            type="button"
            className="btn btn--primary"
            disabled={submitting || !selectedProductId || !code.trim() || !name.trim()}
            onClick={handleSubmit}
          >
            {submitting ? '创建中…' : '创建套餐'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlanEditPanel({ planId, onClose, onSaved }: { planId: string; onClose: () => void; onSaved: () => void }) {
  const plan = useAsync(useCallback(() => getPlan(planId), [planId]), [planId]);
  const [name, setName] = useState('');
  const [maxDevices, setMaxDevices] = useState(1);
  const [durationDays, setDurationDays] = useState<number | null>(365);
  const [offlineGrace, setOfflineGrace] = useState(604800);
  const [isActive, setIsActive] = useState(true);
  const [versionConstraint, setVersionConstraint] = useState('');
  const [entitlements, setEntitlements] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // 影响面预览（P-05，T2）：该套餐当前生效（active）授权数——保存前告知影响范围
  const impact = useAsync(
    useCallback(
      () => (loaded ? listLicenses({ plan_id: planId, status: 'active', limit: 1 }) : Promise.resolve({ items: [], total: 0, page: 1, limit: 1, hasMore: false })),
      [planId, loaded],
    ),
    [planId, loaded],
  );
  const impactedCount = impact.state.kind === 'ready' ? impact.state.data.total : null;

  if (plan.state.kind === 'ready' && !loaded) {
    const p = plan.state.data;
    setName(p.name);
    setMaxDevices(p.max_devices ?? 1);
    setDurationDays(p.duration_days ?? null);
    setOfflineGrace(p.offline_grace_seconds ?? 86400);
    setIsActive(p.is_active);
    setVersionConstraint(p.version_constraint ?? '');
    setEntitlements(Object.fromEntries(Object.entries(p.entitlements).map(([k, v]) => [k, String(v)])));
    setLoaded(true);
  }

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const parsedEntitlements: Record<string, boolean | number | string> = {};
      for (const [k, v] of Object.entries(entitlements)) {
        if (v === 'true' || v === 'false') parsedEntitlements[k] = v === 'true';
        else if (/^\d+$/.test(v)) parsedEntitlements[k] = parseInt(v, 10);
        else parsedEntitlements[k] = v;
      }
      await updatePlan(planId, {
        name,
        max_devices: maxDevices,
        duration_days: durationDays,
        offline_grace_seconds: offlineGrace,
        is_active: isActive,
        version_constraint: versionConstraint || null,
        entitlements: parsedEntitlements as Record<string, boolean | number | string>,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const addEntitlement = () => {
    if (!newKey.trim()) return;
    setEntitlements({ ...entitlements, [newKey.trim()]: newValue });
    setNewKey('');
    setNewValue('');
  };

  const removeEntitlement = (key: string) => {
    const next = { ...entitlements };
    delete next[key];
    setEntitlements(next);
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel__head">
          <h2 className="panel__title">编辑套餐</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          {plan.state.kind === 'loading' ? (
            <p className="text-muted">加载中…</p>
          ) : plan.state.kind === 'failed' ? (
            <ErrorBlock message={describeError(plan.state.error, '加载套餐')} onRetry={plan.reload} />
          ) : (
            <>
              <div className="field">
                <label>套餐名称</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="field">
                <label>设备上限</label>
                <input type="number" min={1} value={maxDevices} onChange={(e) => setMaxDevices(Math.max(1, Number(e.target.value) || 1))} />
              </div>
              <div className="field">
                <label>有效期（天，留空=永久）</label>
                <input type="number" min={0} value={durationDays ?? ''} onChange={(e) => setDurationDays(e.target.value ? Number(e.target.value) : null)} placeholder="留空=永久" />
              </div>
              <div className="field">
                <label>离线宽限（秒）</label>
                <input type="number" min={21600} value={offlineGrace} onChange={(e) => setOfflineGrace(Math.max(21600, Number(e.target.value) || 21600))} />
              </div>
              <div className="field">
                <label>版本约束（可选）</label>
                <input type="text" value={versionConstraint} onChange={(e) => setVersionConstraint(e.target.value)} placeholder="例: >=2.0.0 <3.0.0" />
              </div>
              <div className="field">
                <label>
                  <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                  {' '}启用
                </label>
              </div>

              <div className="panel__section">
                <h3 className="panel__section-title">权益配置</h3>
                {Object.entries(entitlements).map(([key, value]) => (
                  <div key={key} className="entitlement-row">
                    <code className="entitlement-row__key">{key}</code>
                    <span className="entitlement-row__value">{value}</span>
                    <button type="button" className="btn btn--quiet btn--icon" onClick={() => removeEntitlement(key)}>
                      <Icon name="x" size={ICON_SIZE.inline} />
                    </button>
                  </div>
                ))}
                <div className="entitlement-add">
                  <input type="text" value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="键名" className="entitlement-add__key" />
                  <input type="text" value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="值" className="entitlement-add__value" />
                  <button type="button" className="btn btn--quiet" onClick={addEntitlement}>添加</button>
                </div>
              </div>

              {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}

              {impactedCount !== null && (
                <div className={`t2-band ${impactedCount > 0 ? '' : ''}`} role="note">
                  本次修改将影响 <strong>{impactedCount}</strong> 个已生效授权
                  {impactedCount > 0 && '（套餐快照已随授权签发，新值只影响后续签发与续期）'}
                </div>
              )}

              <button type="button" className="btn btn--primary" disabled={submitting} onClick={handleSave}>
                {submitting ? '保存中…' : '保存修改'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}